import React from 'react';
import './loader.scss';

export interface LoaderProps {
  /** Spinner diameter in px (the whole orbital footprint). Default 120. */
  size?: number;
  /** Optional extra class. */
  className?: string;
}

/**
 * Center orbital spinner — three counter-rotating rings around a pulsing globe.
 * Pure presentational atom: no overlay, caption, or progress. Compose
 * `LoaderMolecule` when you need a full-area backdrop, a caption, or progress.
 *
 * The whole figure scales from a single `--loader-spinner-size` CSS variable
 * (driven by the `size` prop), so the rings + globe stay proportional at any size.
 */
const Loader: React.FC<LoaderProps> = ({ size = 120, className = '' }) => {
  return (
    <div
      className={`loader ${className}`.trim()}
      style={{ ['--loader-spinner-size' as string]: `${size}px` }}
      aria-hidden="true"
    >
      <div className="loader__ring loader__ring--outer" />
      <div className="loader__ring loader__ring--middle" />
      <div className="loader__ring loader__ring--inner" />
      <div className="loader__center">
        <div className="loader__globe">
          <svg viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="30" cy="30" r="28" stroke="url(#loaderGlobeGradient)" strokeWidth="2" opacity="0.3" />
            <circle cx="30" cy="30" r="20" stroke="url(#loaderGlobeGradient)" strokeWidth="1.5" opacity="0.5" />
            <path
              d="M30 2 C30 2, 15 15, 15 30 C15 45, 30 58, 30 58"
              stroke="url(#loaderGlobeGradient)"
              strokeWidth="1.5"
              opacity="0.4"
              fill="none"
            />
            <path
              d="M30 2 C30 2, 45 15, 45 30 C45 45, 30 58, 30 58"
              stroke="url(#loaderGlobeGradient)"
              strokeWidth="1.5"
              opacity="0.4"
              fill="none"
            />
            <line x1="2" y1="30" x2="58" y2="30" stroke="url(#loaderGlobeGradient)" strokeWidth="1.5" opacity="0.4" />
            <ellipse cx="30" cy="30" rx="28" ry="12" stroke="url(#loaderGlobeGradient)" strokeWidth="1.5" opacity="0.4" fill="none" />
            <defs>
              <linearGradient id="loaderGlobeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10B6FE" />
                <stop offset="100%" stopColor="#377FC4" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </div>
    </div>
  );
};

export default Loader;
